import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Heart, Eye, MessageCircle, Trash2, PenLine, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useStore } from "@/store/useStore";
import { catLabel, mediaUrl, fmtDate } from "@/components/blog/BlogFeed";
import { API_URL } from "@/config/api";

const VideoBlock = ({ video }) => {
  if (!video) return null;
  if (video.provider === "file") {
    return <video src={mediaUrl(video.embed)} controls playsInline className="w-full rounded-xl bg-black aspect-video" data-testid="post-video-file" />;
  }
  if (video.provider === "link") {
    return <a href={video.embed} target="_blank" rel="noreferrer" className="text-primary underline text-sm">{video.embed}</a>;
  }
  return (
    <div className="aspect-video w-full overflow-hidden rounded-xl bg-black" data-testid={`post-video-${video.provider}`}>
      <iframe src={video.embed} title="video" className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
    </div>
  );
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useStore();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const authed = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetch(`${API_URL}/api/blog/posts/${slug}`, { headers: authed })
      .then(async (r) => { if (!r.ok) throw new Error(); const p = await r.json(); setPost(p); return p; })
      .then((p) => fetch(`${API_URL}/api/blog/posts/${p.id}/comments`).then((r) => r.json()).then(setComments))
      .catch(() => { toast.error(t("blog.notFound")); navigate("/blog"); });
  }, [slug]);

  const like = async () => {
    if (!token) return navigate("/login");
    const d = await fetch(`${API_URL}/api/blog/posts/${post.id}/like`, { method: "POST", headers: authed }).then((r) => r.json());
    setPost({ ...post, liked: d.liked, likes_count: d.likes_count });
  };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/blog/posts/${post.id}/comments`, { method: "POST", headers: { ...authed, "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setComments([...comments, d]); setText("");
    } catch (e) { toast.error(e.message || t("common.error")); } finally { setSending(false); }
  };

  const removeComment = async (id) => {
    await fetch(`${API_URL}/api/blog/comments/${id}`, { method: "DELETE", headers: authed });
    setComments(comments.filter((c) => c.id !== id));
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: post.title, url }); return; } catch (_) {} }
    navigator.clipboard.writeText(url); toast.success(t("ref.copied"));
  };

  if (!post) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const canEdit = user && (user.is_admin || user.id === post.author_id);
  const lang = i18n.language;

  return (
    <article className="max-w-3xl mx-auto space-y-5 px-1 sm:px-0 pb-10" data-testid="blog-post-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/blog")}><ArrowLeft className="h-4 w-4 mr-2" />{t("common.back")}</Button>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={share} data-testid="post-share-btn"><Share2 className="h-4 w-4" /></Button>
          {canEdit && <Button variant="outline" size="sm" onClick={() => navigate(`/blog/edit/${post.id}`)} data-testid="post-edit-btn"><PenLine className="h-4 w-4 mr-1" />{t("common.edit", "Edit")}</Button>}
        </div>
      </div>
      {post.status !== "published" && (
        <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-3 text-sm text-yellow-500" data-testid="post-status-banner">{t(`blog.status.${post.status}`)}</div>
      )}
      {post.cover_url && <img src={mediaUrl(post.cover_url)} alt="" className="w-full rounded-xl object-cover max-h-[420px]" />}
      <div className="space-y-2">
        <span className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide">{catLabel(post.category, lang)}</span>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight" data-testid="post-title">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="text-primary font-semibold">{post.author_title ? `${post.author_title} ` : ""}{post.author_username}</span>
          <span>{fmtDate(post.published_at || post.created_at, lang)}</span>
          <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{post.views}</span>
          <button onClick={like} className={`flex items-center gap-1 ${post.liked ? "text-red-500" : ""}`} data-testid="post-like-btn">
            <Heart className={`h-3.5 w-3.5 ${post.liked ? "fill-red-500" : ""}`} />{post.likes_count}
          </button>
          <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" />{comments.length}</span>
        </div>
        {post.tags?.length > 0 && <div className="flex flex-wrap gap-1.5">{post.tags.map((tg) => <span key={tg} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">{tg}</span>)}</div>}
      </div>
      <VideoBlock video={post.video} />
      <div className="prose-sm sm:prose-base max-w-none whitespace-pre-wrap leading-relaxed text-foreground/90" data-testid="post-content">{post.content}</div>

      <section className="space-y-3 border-t border-border/50 pt-5" data-testid="post-comments">
        <h2 className="text-base md:text-lg font-bold">{t("blog.comments")} ({comments.length})</h2>
        {comments.map((c) => (
          <div key={c.id} className="rounded-lg bg-muted/30 p-3 text-sm" data-testid={`comment-${c.id}`}>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span className={c.is_admin ? "text-primary font-semibold" : "font-semibold text-foreground"}>{c.username}</span>
              <span className="flex items-center gap-2">{fmtDate(c.created_at, lang)}
                {user && (user.is_admin || user.id === c.user_id) && <button onClick={() => removeComment(c.id)} data-testid={`comment-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 hover:text-red-500" /></button>}
              </span>
            </div>
            <p className="whitespace-pre-wrap">{c.text}</p>
          </div>
        ))}
        {token ? (
          <div className="space-y-2">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("blog.commentPlaceholder")} rows={3} data-testid="comment-input" />
            <Button onClick={send} disabled={sending || !text.trim()} size="sm" data-testid="comment-send-btn">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("blog.send")}</Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => navigate("/login")}>{t("blog.loginToComment")}</Button>
        )}
      </section>
    </article>
  );
}
